// Dashboard and report read models (M7f). All SECURITY INVOKER SQL: RLS decides the rows, and each is scoped to
// the signed-in account. Money-derived numbers (utilisation) only come back non-zero for managers.
import { useQuery } from '@tanstack/react-query';
import type { AssetOverview } from '@fleetspoke/core/types';
import { supabase } from '../lib/supabase';

export type ActionKind =
  | 'transfer_awaiting'
  | 'transfer_overdue'
  // dispatched > 24 h ago with no expected time, not arrived (at the origin)
  | 'transfer_stuck'
  | 'asset_missing'
  | 'gateway_offline'
  | 'count_open'
  | 'count_due'
  | 'count_overdue'
  | 'tag_low_battery'
  | 'deployment_overdue'
  // requests from a warehouse (CR_20260915_Request_Qty): the viewer's queue, the same as the Requests menu chip
  | 'request_pending'
  | 'request_to_prepare'
  | 'request_late'
  // a retired asset whose tag is still on: warehouse staff at its site (CR_20260920_Asset_Register A15)
  | 'retire_tag_removal'
  // a certificate inside its reminder lead (warning) or expired (critical) — CR_20260921_Asset_Lifecycle L10
  | 'cert_expiring';

export interface ActionItem {
  kind: ActionKind;
  severity: 'critical' | 'warning' | 'info';
  ref_id: string;
  site_id: string | null;
  since: string | null;
  params: Record<string, string | number | string[] | null>;
}

export interface MovementDay {
  day: string;
  arrivals: number;
  departures: number;
}

export interface Utilisation {
  category_id: string | null;
  category_name: string;
  assets: number;
  asset_days: number;
  deployed_days: number;
  utilisation: number | null;
  /** CR_20260920_Asset_Register phase B: assets with a purchase value, and their sum (numeric → string) */
  valued_assets?: number;
  value?: string | number | null;
}

export interface LocationEvent {
  id: number;
  event: string;
  at: string;
  source: string | null;
  vehicle_ref: string | null;
  assets: { asset_code: string; description: string | null } | null;
  sites: { name: string } | null;
}

export interface TagRow {
  id: string;
  type: 'ble_beacon' | 'ble_sensor' | 'gps_tracker';
  identifier: string;
  battery_pct: number | null;
  last_seen_at: string | null;
  asset_id: string | null;
  assets: { asset_code: string; description: string | null } | null;
}

export interface GatewayPoint {
  id: string;
  identifier: string;
  display_name: string | null;
  is_online: boolean;
  last_seen_at: string | null;
  site_id: string | null;
}

async function data<T>(
  q: PromiseLike<{ data: unknown; error: { message: string; code?: string } | null }>,
): Promise<T> {
  const { data: d, error } = await q;
  if (error) throw Object.assign(new Error(error.message), { code: error.code });
  return d as T;
}

export const dashboardKeys = {
  actions: ['needs-action'] as const,
  stateCounts: ['asset-state-counts'] as const,
  movements: (days: number) => ['movements-per-day', days] as const,
  utilisation: (from: string, to: string) => ['utilisation', from, to] as const,
};

export function useNeedsAction() {
  return useQuery({
    queryKey: dashboardKeys.actions,
    queryFn: () => data<ActionItem[]>(supabase().rpc('needs_action', { p_limit: 100 })),
    refetchInterval: 60_000,
  });
}

/** Assets per display state (the dashboard tiles), counted in SQL. */
export function useAssetStateCounts() {
  return useQuery({
    queryKey: dashboardKeys.stateCounts,
    queryFn: async () => {
      const rows = await data<{ display_state: string; assets: number }[]>(
        supabase().rpc('asset_state_counts'),
      );
      return Object.fromEntries(rows.map((r) => [r.display_state, r.assets])) as Record<
        string,
        number
      >;
    },
  });
}

export function useMovementsPerDay(days = 30) {
  return useQuery({
    queryKey: dashboardKeys.movements(days),
    queryFn: () => data<MovementDay[]>(supabase().rpc('movements_per_day', { p_days: days })),
  });
}

export function useUtilisation(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: dashboardKeys.utilisation(from, to),
    queryFn: () =>
      data<Utilisation[]>(supabase().rpc('utilisation_by_category', { p_from: from, p_to: to })),
    enabled: enabled && !!from && !!to && from <= to,
  });
}

/** Arrivals / departures in [fromIso, toIso) — newest first, capped. */
export function useLocationEvents(fromIso: string, toIso: string, enabled = true, limit = 5000) {
  return useQuery({
    queryKey: ['location-events', fromIso, toIso, limit],
    enabled,
    queryFn: () =>
      data<LocationEvent[]>(
        supabase()
          .from('asset_location_history')
          .select(
            'id, event, at, source, vehicle_ref, assets(asset_code, description), sites(name)',
          )
          .gte('at', fromIso)
          .lt('at', toIso)
          .order('at', { ascending: false })
          .limit(limit),
      ),
  });
}

export function useMissingAssets(enabled = true) {
  return useQuery({
    queryKey: ['assets', 'missing'],
    enabled,
    queryFn: () =>
      data<AssetOverview[]>(
        supabase()
          .from('asset_overview')
          .select('*')
          .eq('display_state', 'missing')
          .eq('asset_status', 'active')
          .order('since_at', { ascending: true }),
      ),
  });
}

export function useTagHealth(enabled = true) {
  return useQuery({
    queryKey: ['tag-health'],
    enabled,
    queryFn: () =>
      data<TagRow[]>(
        supabase()
          .from('tags')
          .select(
            'id, type, identifier, battery_pct, last_seen_at, asset_id, assets(asset_code, description)',
          )
          .in('type', ['ble_beacon', 'ble_sensor', 'gps_tracker'])
          .eq('status', 'allocated')
          .order('identifier'),
      ),
  });
}

/** Allocated site gateways (the map places them at their site). */
export function useGatewayPoints() {
  return useQuery({
    queryKey: ['gateway-points'],
    queryFn: () =>
      data<GatewayPoint[]>(
        supabase()
          .from('gateways')
          .select('id, identifier, display_name, is_online, last_seen_at, site_id')
          .eq('type', 'fixed')
          .eq('status', 'allocated')
          .not('site_id', 'is', null),
      ),
    refetchInterval: 60_000,
  });
}

/** Assets that have a position (GPS trackers, lorry fixes) — the map's moving layer. */
export function useMapAssets() {
  return useQuery({
    queryKey: ['map-assets'],
    queryFn: () =>
      data<AssetOverview[]>(
        supabase()
          .from('asset_overview')
          .select('*')
          .not('lat', 'is', null)
          .not('lng', 'is', null)
          .eq('asset_status', 'active')
          .limit(2000),
      ),
    refetchInterval: 60_000,
  });
}
